/**
 * Mobile App Service
 * Offline-first architecture for support worker mobile app
 * Includes GPS geofencing, lone worker safety, and offline sync
 */

import LocalStorageService from '../storage/LocalStorageService';
import OfflineService from '../utils/OfflineService';
import SyncEngine from '../utils/SyncEngine';

class MobileAppService {
  constructor() {
    this.storageKey = 'mobile_cache';
    this.geofenceRadius = 100; // 100 meters
    this.safetyCheckInterval = 3600000; // 1 hour in milliseconds
  }

  /**
   * Initialize mobile app with offline caching
   */
  async initializeMobileApp(userId) {
    try {
      // Cache today's roster
      await this.cacheTodaysRoster(userId);
      
      // Cache participant profiles for assigned shifts
      await this.cacheParticipantProfiles(userId);
      
      // Cache critical alerts
      await this.cacheCriticalAlerts(userId);
      
      // Initialize GPS tracking
      await this.initializeGPSTracking(userId);
      
      // Initialize lone worker safety
      await this.initializeLoneWorkerSafety(userId);
      
      return { success: true, message: 'Mobile app initialized' };
    } catch (error) {
      console.error('Error initializing mobile app:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Cache today's roster for offline access
   */
  async cacheTodaysRoster(userId) {
    try {
      // Get today's roster from main service
      const today = new Date().toISOString().split('T')[0];
      const roster = LocalStorageService.get('roster', []).filter(
        shift => shift.workerId === userId && shift.date === today
      );
      
      // Store in mobile cache
      LocalStorageService.set(`${this.storageKey}_roster_${userId}`, roster);
      
      return { success: true, data: roster };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Cache participant profiles for assigned shifts
   */
  async cacheParticipantProfiles(userId) {
    try {
      const roster = LocalStorageService.get(`${this.storageKey}_roster_${userId}`, []);
      const participantIds = [...new Set(roster.map(shift => shift.participantId))];
      
      const residents = LocalStorageService.get('residents', []);
      const profiles = residents.filter(r => participantIds.includes(r.id));
      
      // Cache critical information only
      const criticalProfiles = profiles.map(profile => ({
        id: profile.id,
        name: profile.name,
        photo: profile.photo,
        room: profile.room,
        allergies: profile.allergies || [],
        fallRisk: profile.fallRisk,
        cognitiveStatus: profile.cognitiveStatus,
        mobility: profile.mobility,
        criticalAlerts: this.extractCriticalAlerts(profile),
        communicationPreferences: profile.communicationPreferences || 'Verbal',
        emergencyContact: profile.emergencyContact,
        prescriptions: profile.prescriptions || []
      }));
      
      LocalStorageService.set(`${this.storageKey}_profiles_${userId}`, criticalProfiles);
      
      return { success: true, data: criticalProfiles };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Extract critical alerts from profile
   */
  extractCriticalAlerts(profile) {
    const alerts = [];
    
    if (profile.allergies && profile.allergies.length > 0) {
      alerts.push({
        type: 'allergy',
        severity: 'high',
        message: `Allergies: ${profile.allergies.join(', ')}`
      });
    }
    
    if (profile.fallRisk === 'High') {
      alerts.push({
        type: 'fall_risk',
        severity: 'high',
        message: 'High fall risk - use fall prevention measures'
      });
    }
    
    if (profile.primaryDiagnosis?.some(d => d.includes('Epilepsy'))) {
      alerts.push({
        type: 'epilepsy',
        severity: 'critical',
        message: 'Epilepsy - know seizure protocol'
      });
    }
    
    if (profile.behavioralNotes?.toLowerCase().includes('aggressive')) {
      alerts.push({
        type: 'behavioral',
        severity: 'medium',
        message: 'Behavioral concerns - follow care plan'
      });
    }
    
    return alerts;
  }

  /**
   * Cache critical alerts
   */
  async cacheCriticalAlerts(userId) {
    try {
      const profiles = LocalStorageService.get(`${this.storageKey}_profiles_${userId}`, []);
      const allAlerts = profiles.flatMap(p => p.criticalAlerts || []);
      
      LocalStorageService.set(`${this.storageKey}_alerts_${userId}`, allAlerts);
      
      return { success: true, data: allAlerts };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Initialize GPS tracking
   */
  async initializeGPSTracking(userId) {
    try {
      if (!navigator.geolocation) {
        return { success: false, error: 'Geolocation not supported' };
      }
      
      // Request location permission
      const position = await this.getCurrentPosition();
      
      if (position) {
        LocalStorageService.set(`${this.storageKey}_gps_${userId}`, {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          timestamp: new Date().toISOString(),
          accuracy: position.coords.accuracy
        });
      }
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get current GPS position
   */
  getCurrentPosition() {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        position => resolve(position),
        error => reject(error),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  }

  /**
   * Check if worker is within geofence of participant location
   */
  async checkGeofence(participantId, workerLocation) {
    try {
      const residents = LocalStorageService.get('residents', []);
      const participant = residents.find(r => r.id === participantId);
      
      if (!participant || !participant.address) {
        return { success: false, error: 'Participant address not found' };
      }
      
      // In real implementation, geocode address and calculate distance
      // For now, simulate geofence check
      const distance = this.calculateDistance(
        workerLocation.latitude,
        workerLocation.longitude,
        participant.address.latitude || -37.8136, // Default Melbourne
        participant.address.longitude || 144.9631
      );
      
      const withinGeofence = distance <= this.geofenceRadius;
      
      return {
        success: true,
        withinGeofence,
        distance,
        radius: this.geofenceRadius
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Calculate distance between two coordinates (Haversine formula)
   */
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371e3; // Earth radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c; // Distance in meters
  }

  /**
   * Initialize lone worker safety features
   */
  async initializeLoneWorkerSafety(userId) {
    try {
      // Set up safety check-in interval
      const safetyConfig = {
        userId,
        enabled: true,
        checkInInterval: this.safetyCheckInterval,
        lastCheckIn: new Date().toISOString(),
        duressEnabled: true,
        emergencyContacts: []
      };
      
      LocalStorageService.set(`${this.storageKey}_safety_${userId}`, safetyConfig);
      
      return { success: true, data: safetyConfig };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Trigger duress/SOS alert
   */
  async triggerDuressAlert(userId, location) {
    try {
      const alert = {
        id: `DUress-${Date.now()}`,
        userId,
        type: 'duress',
        severity: 'critical',
        location: {
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy
        },
        timestamp: new Date().toISOString(),
        status: 'active',
        acknowledged: false
      };
      
      // Store alert
      LocalStorageService.addItem('duress_alerts', alert);
      
      // In real implementation, send SMS and push notification to operations center
      console.log('DUress alert triggered:', alert);
      
      return { success: true, data: alert };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Safety check-in
   */
  async safetyCheckIn(userId, location) {
    try {
      const checkIn = {
        userId,
        location: {
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy: location.accuracy
        },
        timestamp: new Date().toISOString(),
        status: 'ok'
      };
      
      LocalStorageService.addItem(`${this.storageKey}_checkins_${userId}`, checkIn);
      
      // Update last check-in time
      const safetyConfig = LocalStorageService.findById(`${this.storageKey}_safety_${userId}`, userId);
      if (safetyConfig) {
        safetyConfig.lastCheckIn = new Date().toISOString();
        LocalStorageService.updateItem(`${this.storageKey}_safety_${userId}`, userId, safetyConfig);
      }
      
      return { success: true, data: checkIn };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Check for missed safety check-ins
   */
  async checkMissedSafetyCheckIns(userId) {
    try {
      const safetyConfig = LocalStorageService.findById(`${this.storageKey}_safety_${userId}`, userId);
      
      if (!safetyConfig || !safetyConfig.enabled) {
        return { success: true, missed: false };
      }
      
      const lastCheckIn = new Date(safetyConfig.lastCheckIn);
      const now = new Date();
      const timeSinceCheckIn = now - lastCheckIn;
      
      if (timeSinceCheckIn > this.safetyCheckInterval) {
        // Missed check-in - trigger alert
        const alert = {
          id: `MISSED-${Date.now()}`,
          userId,
          type: 'missed_checkin',
          severity: 'high',
          lastCheckIn: safetyConfig.lastCheckIn,
          timeSinceCheckIn: Math.floor(timeSinceCheckIn / 1000 / 60), // minutes
          timestamp: new Date().toISOString()
        };
        
        LocalStorageService.addItem('safety_alerts', alert);
        
        return { success: true, missed: true, alert };
      }
      
      return { success: true, missed: false };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Sync offline data when connection restored
   */
  async syncOfflineData(userId) {
    try {
      // Get offline queue
      const offlineQueue = LocalStorageService.get(`${this.storageKey}_queue_${userId}`, []);
      
      if (offlineQueue.length === 0) {
        return { success: true, synced: 0 };
      }
      
      let synced = 0;
      const errors = [];
      
      for (const item of offlineQueue) {
        try {
          // Sync based on resource type
          const result = await SyncEngine.syncResource(item.resourceType, item.data);
          
          if (result.success) {
            synced++;
          } else {
            errors.push({ item, error: result.error });
          }
        } catch (error) {
          errors.push({ item, error: error.message });
        }
      }
      
      // Clear synced items
      if (synced > 0) {
        LocalStorageService.set(`${this.storageKey}_queue_${userId}`, errors.map(e => e.item));
      }
      
      return {
        success: true,
        synced,
        errors: errors.length,
        errorDetails: errors
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Add item to offline queue
   */
  addToOfflineQueue(userId, resourceType, data) {
    try {
      const queue = LocalStorageService.get(`${this.storageKey}_queue_${userId}`, []);
      
      queue.push({
        id: `QUEUE-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        resourceType,
        data,
        timestamp: new Date().toISOString(),
        synced: false
      });
      
      LocalStorageService.set(`${this.storageKey}_queue_${userId}`, queue);
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Voice-to-text note recording
   */
  async recordVoiceNote(userId, participantId) {
    try {
      // In real implementation, use Web Speech API or native speech recognition
      // For now, return structure
      return {
        success: true,
        message: 'Voice recording started. In production, this would use Web Speech API.',
        note: {
          id: `VOICE-${Date.now()}`,
          userId,
          participantId,
          type: 'voice_note',
          timestamp: new Date().toISOString(),
          status: 'recording'
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Validate shift completion (prevent clock off until tasks complete)
   */
  async validateShiftCompletion(userId, shiftId) {
    try {
      // Get shift tasks
      const shift = LocalStorageService.findById('roster', shiftId);
      
      if (!shift) {
        return { success: false, error: 'Shift not found' };
      }
      
      // Check mandatory tasks
      const mandatoryTasks = shift.mandatoryTasks || [];
      const completedTasks = shift.completedTasks || [];
      
      const incompleteTasks = mandatoryTasks.filter(
        task => !completedTasks.includes(task.id)
      );
      
      if (incompleteTasks.length > 0) {
        return {
          success: false,
          canClose: false,
          incompleteTasks,
          message: `Cannot close shift. ${incompleteTasks.length} mandatory task(s) incomplete.`
        };
      }
      
      // Check medication tasks (if integrated)
      const medicationValidation = await this.validateMedicationTasks(shift.participantId, shiftId);
      
      if (!medicationValidation.canClose) {
        return medicationValidation;
      }
      
      return {
        success: true,
        canClose: true,
        message: 'All mandatory tasks completed'
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Validate medication tasks
   */
  async validateMedicationTasks(participantId, shiftId) {
    try {
      // Import medication roster integration
      const { validateMedicationTasks } = await import('../utils/MedicationRosterIntegration');
      
      const shift = LocalStorageService.findById('roster', shiftId);
      const shiftTimeRange = {
        startTime: shift.startTime,
        endTime: shift.endTime
      };
      
      const result = await validateMedicationTasks(participantId, shiftId, shiftTimeRange);
      
      return result;
    } catch (error) {
      // If medication validation fails, allow shift closure (graceful degradation)
      return { canClose: true, warning: 'Medication validation unavailable' };
    }
  }
}

export default new MobileAppService();

